import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { getImageUrl } from '../utils/api';

const CartContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding-top: 100px;
  padding-bottom: 60px;
`;

const CartContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const CartHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const CartTitle = styled.h1`
  font-size: 36px;
  color: #333;
  margin-bottom: 10px;
`;

const CartSubtitle = styled.p`
  color: #666;
  font-size: 18px;
`;

const CartGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const CartItems = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const CartItem = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 0;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

const ItemImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 20px;
  
  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    margin-right: 0;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ItemName = styled.h3`
  font-size: 18px;
  color: #333;
  margin: 0 0 5px 0;
`;

const ItemBrand = styled.p`
  color: #666;
  font-size: 14px;
  margin: 0 0 10px 0;
`;

const ItemPrice = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 20px;
  
  @media (max-width: 768px) {
    margin: 0;
    justify-content: space-between;
    width: 100%;
  }
`;

const QuantityButton = styled.button`
  background: #f1f3f4;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #333;
  transition: background 0.2s ease;
  
  &:hover {
    background: #e8eaed;
  }
  
  &:disabled {
    background: #f8f9fa;
    color: #ccc;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 60px;
  text-align: center;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const RemoveButton = styled.button`
  background: #ff4444;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s ease;
  
  &:hover {
    background: #cc0000;
  }
`;

const CartSummary = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: fit-content;
  position: sticky;
  top: 100px;
`;

const SummaryTitle = styled.h2`
  font-size: 24px;
  color: #333;
  margin: 0 0 20px 0;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  
  &.total {
    font-weight: 600;
    font-size: 18px;
    color: #333;
    border-top: 1px solid #eee;
    padding-top: 15px;
    margin-top: 20px;
  }
`;

const SummaryLabel = styled.span`
  color: #666;
`;

const SummaryValue = styled.span`
  color: #333;
`;

const PointsInfo = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin: 15px 0;
  border-left: 4px solid #ff6b35;
`;

const DiscountInfo = styled.div`
  background: #e8f5e9;
  border-radius: 8px;
  padding: 15px;
  margin: 15px 0;
  border-left: 4px solid #4CAF50;
`;

const DiscountRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DiscountLabel = styled.span`
  color: #666;
  font-size: 14px;
`;

const DiscountValue = styled.span`
  color: #2e7d32;
  font-weight: 600;
  font-size: 14px;
`;

const PointsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const PointsLabel = styled.span`
  color: #666;
  font-size: 14px;
`;

const PointsValue = styled.span`
  color: #ff6b35;
  font-weight: 600;
  font-size: 14px;
`;

const PointsInput = styled.input`
  width: 80px;
  padding: 5px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const PointsButton = styled.button`
  background: #ff6b35;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
  margin-left: 5px;
  
  &:hover {
    background: #e55a2b;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const FinalPrice = styled.div`
  background: #e8f5e8;
  border-radius: 8px;
  padding: 15px;
  margin: 15px 0;
  border-left: 4px solid #4CAF50;
`;

const FinalPriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #2e7d32;
`;

const CheckoutButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 15px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 20px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  }
`;

const ContinueShoppingButton = styled(Link)`
  display: inline-block;
  width: 100%;
  background: transparent;
  color: #ff6b35;
  border: 2px solid #ff6b35;
  border-radius: 8px;
  padding: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 10px;
  text-decoration: none;
  text-align: center;
  
  &:hover {
    background: #ff6b35;
    color: white;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const EmptyCartIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.3;
`;

const EmptyCartTitle = styled.h2`
  font-size: 24px;
  color: #333;
  margin-bottom: 10px;
`;

const EmptyCartText = styled.p`
  color: #666;
  font-size: 16px;
  margin-bottom: 30px;
`;

const CartPage = () => {
  const { items, totalItems, totalPrice, totalPoints, physicalSubtotal, hasDigital, updateQuantity, removeFromCart, clearCart } = useCart();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  
  const [userPoints, setUserPoints] = useState(0);
  const [pointsToSpend, setPointsToSpend] = useState(0);
  const [maxPointsDiscountPercent, setMaxPointsDiscountPercent] = useState(100);
  const [orderDiscountPercent, setOrderDiscountPercent] = useState(5);

  // Загружаем баллы пользователя и настройки
  useEffect(() => {
    if (isAuthenticated() && user) {
      loadUserPoints();
      loadSettings();
    } else {
      loadSettings();
    }
  }, [isAuthenticated, user]);

  const loadUserPoints = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUserPoints(userData.user?.points || 0);
      }
    } catch (error) {
      console.error('Ошибка загрузки баллов пользователя:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await fetch('/api/settings');
      if (response.ok) {
        const data = await response.json();
        setMaxPointsDiscountPercent(data.settings?.maxPointsDiscountPercent ?? 100);
        setOrderDiscountPercent(data.settings?.orderDiscountPercent ?? 5);
      }
    } catch (error) {
      console.error('Ошибка загрузки настроек:', error);
    }
  };

  const physicalTotal = physicalSubtotal ?? totalPrice;
  const discountAmount = Math.floor(physicalTotal * ((Number(orderDiscountPercent) || 0) / 100));
  const physicalAfterDiscount = Math.max(0, physicalTotal - discountAmount);
  const maxPointsToSpend = Math.floor(physicalAfterDiscount * (maxPointsDiscountPercent / 100));

  const handlePointsChange = (value) => {
    const points = Math.max(0, Math.min(parseInt(value) || 0, Math.min(userPoints, maxPointsToSpend)));
    setPointsToSpend(points);
  };

  const handleMaxPoints = () => {
    setPointsToSpend(Math.min(userPoints, maxPointsToSpend));
  };

  const handleQuantityChange = (productId, newQuantity) => {
    const quantity = parseInt(newQuantity);
    if (quantity > 0 && quantity <= 99) {
      updateQuantity(productId, quantity);
    }
  };

  const handleQuantityIncrease = (productId, currentQuantity) => {
    if (currentQuantity < 99) {
      updateQuantity(productId, currentQuantity + 1);
    }
  };

  const handleQuantityDecrease = (productId, currentQuantity) => {
    if (currentQuantity > 1) {
      updateQuantity(productId, currentQuantity - 1);
    }
  };

  const handleCheckout = () => {
    if (!isAuthenticated()) {
      // Если пользователь не авторизован, перенаправляем на страницу входа
      navigate('/login');
      return;
    }

    // Сохраняем данные корзины, баллов и скидки в localStorage для страницы оформления
    const checkoutData = {
      items: items,
      totalPrice: totalPrice,
      totalPoints: totalPoints,
      pointsToSpend: pointsToSpend,
      discountAmount: discountAmount,
      orderDiscountPercent: orderDiscountPercent,
      finalPrice: totalPrice - discountAmount - pointsToSpend,
      hasDigital: hasDigital || false,
      physicalSubtotal: physicalTotal
    };
    
    localStorage.setItem('checkoutData', JSON.stringify(checkoutData));
    
    // Перенаправляем на страницу оформления заказа
    navigate('/checkout');
  };

  if (items.length === 0) {
    return (
      <CartContainer>
        <CartContent>
          <CartHeader>
            <CartTitle>Корзина</CartTitle>
            <CartSubtitle>Ваша корзина пуста</CartSubtitle>
          </CartHeader>
          
          <EmptyCart>
            <EmptyCartIcon>🛒</EmptyCartIcon>
            <EmptyCartTitle>Корзина пуста</EmptyCartTitle>
            <EmptyCartText>
              Добавьте товары в корзину, чтобы продолжить покупки
            </EmptyCartText>
            <ContinueShoppingButton to="/catalog">
              Перейти к покупкам
            </ContinueShoppingButton>
          </EmptyCart>
        </CartContent>
      </CartContainer>
    );
  }

  return (
    <CartContainer>
      <CartContent>
        <CartHeader>
          <CartTitle>Корзина</CartTitle>
          <CartSubtitle>
            {totalItems} {totalItems === 1 ? 'товар' : totalItems < 5 ? 'товара' : 'товаров'} в корзине
          </CartSubtitle>
        </CartHeader>
        
        <CartGrid>
          <CartItems>
            {items.map((item) => (
              <CartItem key={item.id}>
                <ItemImage 
                  src={item.image || getImageUrl(item.featured_image) || '/nofoto.png'} 
                  alt={item.name}
                  onError={(e) => {
                    if (!e.target.dataset.fallbackApplied) {
                      e.target.dataset.fallbackApplied = 'true';
                      e.target.src = '/nofoto.png';
                    }
                  }}
                />
                
                <ItemInfo>
                  <ItemName>{item.name}</ItemName>
                  <ItemBrand>{item.brand}</ItemBrand>
                  <ItemPrice>{item.price.toLocaleString()} ₽</ItemPrice>
                </ItemInfo>
                
                <QuantityControls>
                  <QuantityButton 
                    onClick={() => handleQuantityDecrease(item.id, item.quantity)}
                    disabled={item.quantity <= 1}
                  >
                    −
                  </QuantityButton>
                  
                  <QuantityInput
                    type="number"
                    min="1"
                    max="99"
                    value={item.quantity}
                    onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                  />
                  
                  <QuantityButton 
                    onClick={() => handleQuantityIncrease(item.id, item.quantity)}
                    disabled={item.quantity >= 99}
                  >
                    +
                  </QuantityButton>
                  
                  <RemoveButton onClick={() => removeFromCart(item.id)}>
                    Удалить
                  </RemoveButton>
                </QuantityControls>
              </CartItem>
            ))}
          </CartItems>
          
          <CartSummary>
            <SummaryTitle>Итого</SummaryTitle>
            {hasDigital && (
              <p style={{ fontSize: 13, color: '#856404', background: '#fff3cd', padding: '10px 12px', borderRadius: 8, marginBottom: 12 }}>
                Баллы применяются только к физическим товарам. Цифровые программы оплачиваются полностью.
              </p>
            )}
            
            <SummaryRow>
              <SummaryLabel>Товары ({totalItems})</SummaryLabel>
              <SummaryValue>{totalPrice.toLocaleString()} ₽</SummaryValue>
            </SummaryRow>

            {discountAmount > 0 && (
              <SummaryRow>
                <SummaryLabel>Скидка {orderDiscountPercent}%</SummaryLabel>
                <SummaryValue style={{ color: '#2e7d32' }}>-{discountAmount.toLocaleString()} ₽</SummaryValue>
              </SummaryRow>
            )}
            
            {isAuthenticated() && userPoints > 0 && (
              <PointsInfo>
                <PointsRow>
                  <PointsLabel>Доступно баллов</PointsLabel>
                  <PointsValue>{userPoints}</PointsValue>
                </PointsRow>
                <PointsRow>
                  <PointsLabel>Можно списать (до {maxPointsDiscountPercent}%)</PointsLabel>
                  <PointsValue>{maxPointsToSpend}</PointsValue>
                </PointsRow>
                <PointsRow>
                  <PointsLabel>Списать баллов</PointsLabel>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <PointsInput
                      type="number"
                      min="0"
                      max={Math.min(userPoints, maxPointsToSpend)}
                      value={pointsToSpend}
                      onChange={(e) => handlePointsChange(e.target.value)}
                    />
                    <PointsButton onClick={handleMaxPoints}>
                      Макс
                    </PointsButton>
                  </div>
                </PointsRow>
              </PointsInfo>
            )}
            
            {(pointsToSpend > 0) && (
              <FinalPrice>
                <FinalPriceRow>
                  <span>К доплате</span>
                  <span>{(totalPrice - discountAmount - pointsToSpend).toLocaleString()} ₽</span>
                </FinalPriceRow>
              </FinalPrice>
            )}
            
            <SummaryRow className="total">
              <SummaryLabel>Общая сумма</SummaryLabel>
              <SummaryValue>{(totalPrice - discountAmount - pointsToSpend).toLocaleString()} ₽</SummaryValue>
            </SummaryRow>
            
            <CheckoutButton onClick={handleCheckout}>
              Оформить заказ
            </CheckoutButton>
            
            <ContinueShoppingButton to="/catalog">
              Продолжить покупки
            </ContinueShoppingButton>
            
            <button 
              onClick={clearCart}
              style={{
                width: '100%',
                background: 'transparent',
                color: '#ff4444',
                border: 'none',
                padding: '10px',
                marginTop: '10px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Очистить корзину
            </button>
          </CartSummary>
        </CartGrid>
      </CartContent>
    </CartContainer>
  );
};

export default CartPage; 